"use client";

/* eslint-disable @next/next/no-img-element */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  CONFIG, HERO, GLANCE, SHOWCASE, RITUAL, CONTRAST, PROVEN,
  FILM, CALCULATOR, FAQS, RESERVE, MAP,
} from "./content";
import { captureAttribution, trackClientEvent } from "@aluume/shared/lib/tracking/client";
import { MembershipFaqs } from "./MembershipFaqs";
import { MembershipMap } from "./MembershipMap";
import { MembershipCheckout } from "./MembershipCheckout";

// The whole /100 page body (everything below the shared ScrollHeader). Client
// component so the signature interactions from the approved mockup survive:
// hotspot showcase, time-of-day ritual explorer, contrast cycle, value
// calculator, opening countdown, scroll-reveal + sticky mobile CTA. Per the
// review revisions the hero + showcase are now single static images (no
// crossfade), the calculator is merged into the "at a glance" section, the
// ritual sits on a solid dark ground and the map is a Google embed. Copy +
// figures come entirely from ./content. Colours use the real Aluume design
// system tokens — the mockup's gold accent maps to the shared `tussock` token;
// base theming is data-brand "thermae". TYPE was once the one place /100
// departed from the rest of book.aluume.com — Libre Baskerville + Inconsolata,
// scoped to this route. As of 2026-09-02 that pair is the whole customer app's
// type (app/layout.tsx + app/styles/tokens.css), matching aluume.com, so this
// page is no longer special. Everything here still goes through the
// --font-display / --font-sans tokens, never a raw family name.

const container = "mx-auto w-full max-w-[1280px] px-[clamp(20px,5vw,64px)]";
const sectionPad = "py-[clamp(72px,12vw,168px)]";
const h2Size = "text-[clamp(36px,5.6vw,72px)]";

// Server-driven opening config for the countdown (from /api/membership/opening). iso is the
// target date; the countdown now renders whenever a date exists (the show_countdown toggle was
// removed — owner, 2026-08-31; the API still returns the field, we just no longer read it).
type OpeningState = { iso: string | null };

// ── Small shared bits ─────────────────────────────────────────────────────────
function Eyebrow({ children, dark }: { children: React.ReactNode; dark?: boolean }) {
  return (
    // The short rule that used to sit before the label is gone (owner,
    // 2026-09-03 — "the em dashes before the uppercase headers"). It was never a
    // dash character, which is why the em-dash sweep in 255 did not catch it: it
    // was a 20px x 1px <span> rule. Removing it here clears every eyebrow on the
    // page at once.
    <span className={`text-[12px] uppercase tracking-[0.14em] ${dark ? "text-tussock" : "text-mocha"}`}>
      {children}
    </span>
  );
}

function Render({ base, alt, className, sizes, eager }: { base: string; alt: string; className?: string; sizes: string; eager?: boolean }) {
  return (
    <img
      src={`${base}-1200.jpg`}
      srcSet={`${base}-640.jpg 640w, ${base}-1200.jpg 1200w, ${base}-2400.jpg 2400w`}
      sizes={sizes}
      alt={alt}
      loading={eager ? "eager" : "lazy"}
      className={className}
    />
  );
}

// Scroll-reveal wrapper — fades + lifts content into view once. Falls back to
// visible when IntersectionObserver is unavailable or reduced motion is set.
function Reveal({ children, className }: { children: React.ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // No IntersectionObserver (very old browser) or reduced motion → reveal on the
    // next frame rather than observing. Deferred via rAF so content is never left
    // hidden, and the reveal isn't a synchronous setState in the effect body.
    if (!("IntersectionObserver" in window) || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      const id = requestAnimationFrame(() => setShown(true));
      return () => cancelAnimationFrame(id);
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { setShown(true); io.disconnect(); } });
    }, { threshold: 0.12 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={className}
      style={{ opacity: shown ? 1 : 0, transform: shown ? "none" : "translateY(28px)", transition: "opacity 900ms cubic-bezier(.16,1,.3,1), transform 900ms cubic-bezier(.16,1,.3,1)" }}
    >
      {children}
    </div>
  );
}

// ── Scroll-driven state cycling ───────────────────────────────────────────────
// Advances an index as the section travels through the viewport, so a section
// plays through its states while you scroll instead of sitting on the first one
// (owner, 2026-09-03). A tap takes over PERMANENTLY: once someone has chosen a
// phase, continuing to scroll must not yank it away from them.
function useScrollCycle(count: number, ref: React.RefObject<HTMLElement | null>) {
  const [index, setIndex] = useState(0);
  const locked = useRef(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || count < 2) return;
    let frame = 0;

    const update = () => {
      frame = 0;
      if (locked.current) return;
      const r = node.getBoundingClientRect();
      const vh = window.innerHeight;
      // The cycle spans the section's pass through the viewport, trimmed at both
      // ends (0.75vh in, 0.25vh out) so the first and last states get a real
      // dwell instead of flicking past as the section enters and leaves.
      const p = (vh * 0.75 - r.top) / (r.height + vh * 0.5);
      const next = Math.max(0, Math.min(count - 1, Math.floor(p * count)));
      setIndex((cur) => (cur === next ? cur : next));
    };

    // Scheduled rather than called inline: a synchronous setState in an effect
    // body cascades a second render before paint.
    const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
    schedule();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, [count, ref]);

  const choose = useCallback((next: number) => { locked.current = true; setIndex(next); }, []);
  return [index, choose] as const;
}

// ── Glance "Unlimited" asterisk qualifier note ────────────────────────────────
// The Glance Access tile's "Unlimited" claim carries a trailing asterisk (owner
// 2026-08-29). An asterisked claim is only a defence under the Fair Trading Act
// (s9/s13) when the qualifier is PROXIMATE to the claim — so this footnote is
// rendered in the SAME section as the claim, never link-only and never at the
// page foot. The Inclusions grid that used to restate the claim was replaced by
// the film section (owner, 2026-09-03), so Glance is now the only place on /100
// making it — this footnote must travel with it. The full one-session-per-day
// rule lives in the linked Thermae100 Membership Terms only (owner 2026-08-29 —
// kept off the /100 sales page). `dark` is set for the Glance (ink ground) placement.
function UnlimitedNote({ dark }: { dark?: boolean }) {
  return (
    <p className={`mt-6 text-[12px] font-light ${dark ? "text-ink-on-dark/55" : "text-mocha"}`}>
      *See{" "}
      <a
        href={CONFIG.termsUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`underline ${dark ? "text-tussock" : "text-corten"}`}
      >
        Membership Terms
      </a>
      .
    </p>
  );
}

// ── Hero ──────────────────────────────────────────────────────────────────────
// Single static image (the angled exterior-dusk shot). The overlay is a strong
// full-height scrim so the headline keeps solid contrast against the photo at
// every scroll position and viewport size.
//
// SPACE ABOVE THE PILL: the copy block is bottom-anchored (justify-end), so every
// pixel the section is taller than its content becomes empty photo ABOVE the
// "Only 100 available" pill — 428px of it on a 390×844 phone, more than half the
// first screen (owner, 2026-09-02: "too much space above ONLY 100 AVAILABLE,
// especially on mobile"). The block's own padding-TOP cannot fix that: it sits
// inside a box already pushed to the bottom, so growing or shrinking it just
// moves the box's top edge and leaves the pill where it was. Padding-BOTTOM is
// the lever — it lifts the whole stack off the section floor. Mobile is
// tightened hardest (pb 32px → 20svh ≈ 169px on a 390×844 phone, pill top
// 483 → 346), desktop only trimmed (pb 56px → 12svh ≈ 108px at 1440×900, pill
// top 339 → 287) so it stays airy. Deliberately NOT done by shortening
// min-h-[100svh]: that ends the photo mid-viewport and leaves a flat #393331
// band under it.
function Hero() {
  return (
    // Mobile hero height dialled in with the owner: 100svh felt too tall (too much
    // space above the text) → cropped to 70svh (2026-09-03) → 70svh put the badge too
    // high/tight → 82svh (2026-09-04, "more padding above the ONLY 100 AVAILABLE badge").
    // min-h IS the lever here, NOT the content's pt: the content is justify-end
    // (bottom-anchored) and shorter than the section, so the badge sits at
    // (sectionHeight − contentHeight − pb) — independent of top padding. A taller
    // section pushes the whole content group down, opening space above the badge
    // (filled by more of the building image, which is bottom-pinned). Desktop keeps the
    // full-height hero signed off at 255.
    <section id="top" className="relative flex min-h-[82svh] flex-col justify-end overflow-hidden bg-ink text-ink-on-dark sm:min-h-[100svh]">
      <div className="absolute inset-0 z-0" aria-hidden>
        {/* Single static shot, slowly panned (see the hero-pan keyframes in the page
            body). transform-origin keeps the pavilion in frame through the drift.
            The photo is 1.39 landscape inside a portrait frame, so object-cover
            fits its HEIGHT and crops the sides — there is no vertical overflow to
            re-position. Shrinking the section alone would therefore just scale the
            whole shot down and keep the same amount of sky. Instead the image keeps
            its full-height size (h-[100svh]) and is pinned to the bottom, so the
            section's overflow-hidden takes the missing 30% off the TOP — the empty
            sky goes and the pavilion stays the size it was. */}
        <Render base={HERO.image} alt="" sizes="100vw" eager className="hero-pan absolute bottom-0 left-0 h-[100svh] w-full object-cover sm:h-full" />
      </div>
      <div className="absolute inset-0 z-[1]" aria-hidden style={{ background: "linear-gradient(to top, rgba(20,17,15,.94) 0%, rgba(20,17,15,.78) 38%, rgba(20,17,15,.66) 64%, rgba(20,17,15,.58) 100%)" }} />
      {/* pb: 20svh left ~170px of dead scrim under the CTA on a 390x844 phone (owner,
          2026-09-03). 9svh keeps the pill clear of the sticky mobile CTA bar without the
          gap. Desktop (sm:pb-[12svh]) is untouched — it was signed off at release 255.
          pt here is only HEADER CLEARANCE — it does NOT set the badge's vertical
          position (that's the section min-h; see the note on the section). It only
          matters as a floor if the content ever grows tall enough to fill the section,
          so it stays ≥ the fixed header height. Desktop sm:pt unchanged. */}
      <div className={`relative z-[2] ${container} pb-[9svh] pt-[calc(96px_+_5vw)] sm:pb-[12svh] sm:pt-[calc(96px_+_4vw)]`}>
        <div className="mb-7 inline-flex items-center gap-2.5 rounded-pill border border-white/25 bg-black/25 px-4 py-2 text-[12px] uppercase tracking-[0.1em] backdrop-blur-sm">
          <span className="h-1.5 w-1.5 rounded-full bg-tussock" style={{ boxShadow: "0 0 0 4px rgba(166,149,128,.25)" }} />
          <span>{HERO.badge}</span>
        </div>
        <h1 className="mb-5 max-w-[16ch] text-[clamp(48px,9vw,128px)] font-light leading-[1.02] text-ink-on-dark">
          {HERO.headline} <i className="text-tussock">{HERO.headlineEm}</i>
        </h1>
        <p className="mb-9 max-w-[46ch] text-[clamp(16px,2vw,19px)] font-light text-ink-on-dark/80">{HERO.sub}</p>
        <div className="flex flex-wrap items-center gap-4">
          <a href="#reserve" className="ui-label rounded-pill bg-tussock px-8 py-4 text-ink transition-colors hover:bg-cream-deep">Reserve your place</a>
        </div>
      </div>
    </section>
  );
}

// ── Your founder membership (price + value calculator) ────────────────────────
// Stripped back per review to just the headline price and the "how often will
// you come?" calculator. The buy-in reads as a monthly equivalent ($250/mo)
// with the $3,000 one-time figure as subtext.
function Glance() {
  const [freq, setFreq] = useState(CALCULATOR.default);
  const perSession = CONFIG.priceMembership / (freq * CALCULATOR.weeks);
  const whole = Math.floor(perSession);
  const cents = Math.round((perSession - whole) * 100).toString().padStart(2, "0");
  const saving = CALCULATOR.casualRate - perSession;
  // Slider notch marks — one per whole value across the calculator range (1–5).
  const notches = Array.from({ length: CALCULATOR.max - CALCULATOR.min + 1 }, (_, k) => CALCULATOR.min + k);

  return (
    <section id="membership" className="bg-ink text-ink-on-dark" aria-labelledby="glanceHeading">
      <Reveal className={`${container} ${sectionPad}`}>
        <div className="max-w-[760px]">
          <Eyebrow dark>{GLANCE.eyebrow}</Eyebrow>
          <h2 id="glanceHeading" className={`mt-4 max-w-[18ch] ${h2Size} font-light text-ink-on-dark`}>{GLANCE.heading}</h2>
          <p className="mt-5 max-w-[56ch] text-[19px] font-light text-ink-on-dark/78">{GLANCE.intro}</p>
        </div>

        {/* "How often will you come?" value calculator — slider only (1–5 a week).
            Leads the section (moved above the price/fact tiles per review) so the
            visitor works out their own per-visit cost before the tiles land it. */}
        <div className="mt-12 grid items-center gap-12 border border-[color:var(--rule-on-dark)] bg-white/5 p-[clamp(28px,4vw,56px)] lg:grid-cols-2">
          <div>
            <h3 style={{ fontFamily: "var(--font-display)" }} className="mb-2.5 text-[clamp(21px,2.6vw,30px)] font-light text-ink-on-dark">{CALCULATOR.heading}</h3>
            <p className="mb-6 text-[14px] text-ink-on-dark/70">{CALCULATOR.intro}</p>
            <div className="mb-3 text-[14px] text-ink-on-dark/80"><b className="text-tussock">{freq}</b> {freq === 1 ? "session" : "sessions"} a week</div>
            <input type="range" min={CALCULATOR.min} max={CALCULATOR.max} step={1} value={freq} onChange={(e) => setFreq(parseInt(e.target.value, 10))}
              aria-label="Sessions per week" list="freq-notches" className="w-full accent-tussock" />
            <datalist id="freq-notches">
              {notches.map((n) => <option key={n} value={n} />)}
            </datalist>
            <div className="mt-2 flex justify-between text-[11px] text-ink-on-dark/60">
              {notches.map((n) => (
                <span key={n} className={n === freq ? "text-tussock" : ""}>{n}</span>
              ))}
            </div>
          </div>
          <div className="border-t border-[color:var(--rule-on-dark)] pt-8 text-center lg:border-l lg:border-t-0 lg:pl-12 lg:pt-0">
            <div style={{ fontFamily: "var(--font-display)" }} className="text-[clamp(56px,7vw,96px)] font-light leading-none text-tussock">
              ${whole}<sup className="text-[0.4em]">.{cents}</sup>
            </div>
            <div className="mt-2.5 text-[11px] uppercase tracking-[0.1em] text-ink-on-dark/60">Effective cost per session, year one</div>
            <div className="mt-5 text-[13px] text-ink-on-dark/70">
              {saving > 0 ? (
                <>vs a casual drop-in at roughly <b className="text-tussock">${CALCULATOR.casualRate}</b>. You save <b className="text-tussock">${saving.toFixed(0)}</b> a visit.</>
              ) : (
                <>unlimited, for less than one casual visit a week.</>
              )}
            </div>
          </div>
        </div>

        {/* Price tile + two at-a-glance fact tiles in one even three-across row
            (the "The offer" + "Opens" tiles were removed per review; the calculator
            now leads above). grid-cols-3 stretches all three to equal width/height. */}
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <div className="flex flex-col justify-center border border-[color:var(--rule-on-dark)] bg-white/5 p-[clamp(24px,3vw,40px)]">
            <div style={{ fontFamily: "var(--font-display)" }} className="text-[clamp(40px,5vw,60px)] font-light leading-none text-tussock">
              {GLANCE.price.amount}
              <span className="ml-2 align-baseline text-[0.3em] uppercase tracking-[0.14em] text-ink-on-dark/70">{GLANCE.price.unit}</span>
            </div>
            <div className="mt-3 text-[13px] uppercase tracking-[0.12em] text-ink-on-dark/60">{GLANCE.price.sub}</div>
          </div>
          {GLANCE.items.map((it) => (
            <div key={it.eyebrow} className="flex flex-col justify-center gap-1.5 border border-[color:var(--rule-on-dark)] bg-white/5 p-[clamp(24px,3vw,40px)]">
              <span className="text-[11px] uppercase tracking-[0.16em] text-tussock">{it.eyebrow}</span>
              <b style={{ fontFamily: "var(--font-display)" }} className="text-[clamp(24px,2.8vw,32px)] font-light text-ink-on-dark">{it.value}</b>
              <span className="text-[13px] text-ink-on-dark/60">{it.sub}</span>
            </div>
          ))}
        </div>
        {/* Qualifier for the Glance "Unlimited" Access tile above — proximate, on-page. */}
        <UnlimitedNote dark />
      </Reveal>
    </section>
  );
}

// ── The place — single-image hotspot showcase ─────────────────────────────────
// WHY THE MOBILE PANEL EXISTS (owner, 2026-09-02 — "the popups don't render on
// mobile"). They do render; they are CLIPPED. The image frame is locked to
// aspect-ratio 16/10 with overflow-hidden, so on a 390px phone it is only 244px
// tall, while a popup card is min(280px,78vw) wide and 133–152px tall and is
// positioned from its pin's own top/left percentage. Measured at 390×844, five
// of the six cards spill outside the frame and are cut off by that
// overflow-hidden: "Private sauna" loses 124px of width and 40px of height,
// "Cold plunge steps" and "Floated into position" lose over half their height,
// and "Floated into position" is cut off on the left as well. Only hotspot 0
// (the default) happens to fit. It is not hover-only, not `hidden` at a
// breakpoint and not a z-index problem — so no amount of re-anchoring fixes it
// while the frame stays 244px tall. Below `md` the floating cards are therefore
// dropped and the same content is rendered in a panel beneath the image.
function Showcase() {
  // ONE source of truth for which hotspot is selected — it drives both the
  // desktop popup and the mobile panel, so the copy cannot drift between them
  // (both read SHOWCASE.hotspots[active]). `popupOpen` gates the DESKTOP popup
  // only, preserving the existing behaviour there: first pin open on load,
  // tapping another swaps to it, tapping the open one closes it. The mobile
  // panel ignores `popupOpen` and always shows `active`, so it is never empty.
  const [active, setActive] = useState(0);
  const [popupOpen, setPopupOpen] = useState(true);
  const panelId = "place-detail";
  const current = SHOWCASE.hotspots[active];

  return (
    <section id="place" className="bg-cream" aria-labelledby="placeHeading">
      <Reveal>
        {/* gap is BOTH axes: on desktop it is the gutter between the heading and the
            intro column, but the row wraps on mobile so the same 40px becomes the
            vertical gap the owner marked (2026-09-03). Lower it at the base breakpoint
            only — dropping gap-10 outright would pull the desktop columns together. */}
        <div className={`${container} flex flex-wrap items-end justify-between gap-4 pb-11 pt-[clamp(64px,10vw,120px)] sm:gap-10`}>
          <h2 id="placeHeading" className="max-w-[16ch] text-[clamp(28px,4vw,52px)] font-light">{SHOWCASE.heading}</h2>
          <p className="max-w-[38ch] text-[16px] font-light text-mocha">{SHOWCASE.intro}</p>
        </div>
        <div className="relative w-full overflow-hidden bg-ink" style={{ aspectRatio: "16 / 10", maxHeight: "86vh" }}>
          <Render base={SHOWCASE.image} alt={SHOWCASE.alt} sizes="100vw" className="absolute inset-0 h-full w-full object-cover" />
          {SHOWCASE.hotspots.map((h, hi) => {
            const id = String(hi);
            const isActive = active === hi;
            const isOpen = isActive && popupOpen;
            const leftPct = parseFloat(h.left);
            const anchorRight = leftPct > 60;
            return (
              <div key={id}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    // Re-tapping the selected pin only closes the desktop popup —
                    // `active` is left alone so the mobile panel keeps its content.
                    if (isActive) { setPopupOpen((o) => !o); return; }
                    setActive(hi);
                    setPopupOpen(true);
                  }}
                  aria-label={h.eyebrow}
                  aria-pressed={isActive}
                  aria-controls={panelId}
                  // Selected pin is filled tussock wherever the PANEL is showing (it is the
                  // control for it, so it has to read as selected). The `lg:` half resets it to
                  // the original treatment, leaving every pin on desktop exactly as it was.
                  // Boundary is lg, not md: between 768 and 1024 the floating cards still
                  // clipped inside the 16/10 frame (7px and 42px at 768) — the same failure the
                  // panel exists to fix, so a tablet gets the panel too.
                  className={`absolute z-[5] -ml-[13px] -mt-[13px] flex h-[26px] w-[26px] items-center justify-center rounded-full border backdrop-blur-sm transition-colors lg:border-white/90 lg:bg-black/35 lg:shadow-none ${isActive ? "border-tussock bg-tussock shadow-[0_0_0_5px_rgba(166,149,128,.4)]" : "border-white/90 bg-black/35"}`}
                  style={{ top: h.top, left: h.left }}
                >
                  <span aria-hidden className={`h-1.5 w-1.5 rounded-full lg:bg-cream ${isActive ? "bg-ink" : "bg-cream"}`} />
                  <span aria-hidden className="absolute inset-[-8px] rounded-full border border-white/50" style={{ animation: "hs-pulse 2.6s ease-out infinite" }} />
                </button>
                <div
                  className="absolute z-[6] hidden w-[min(280px,78vw)] bg-cream p-5 text-ink shadow-[0_20px_60px_rgba(0,0,0,.35)] transition-all duration-200 lg:block"
                  style={{
                    top: `calc(${h.top} + 18px)`,
                    left: anchorRight ? undefined : h.left,
                    right: anchorRight ? `calc(100% - ${h.left})` : undefined,
                    opacity: isOpen ? 1 : 0,
                    pointerEvents: isOpen ? "auto" : "none",
                    transform: isOpen ? "none" : "translateY(6px)",
                  }}
                >
                  <div className="mb-2"><Eyebrow>{h.eyebrow}</Eyebrow></div>
                  <p className="m-0 text-[14px] font-light text-mocha">{h.body}</p>
                </div>
              </div>
            );
          })}
        </div>
        {/* MOBILE ONLY — the clipped floating cards are replaced by this panel,
            reading the same SHOWCASE.hotspots entry the desktop popup would show.
            min-h holds the tallest body so swapping pins doesn't jump the page. */}
        <div className={`${container} py-7 lg:hidden`}>
          {/* No rule above the panel (owner, 2026-09-03) — the photo edge already
              separates it. min-h-[126px] STAYS: it holds the tallest hotspot body so
              swapping pins doesn't jump the page. */}
          <div id={panelId} aria-live="polite" className="min-h-[126px] pt-1">
            <Eyebrow>{current.eyebrow}</Eyebrow>
            <p className="mt-2.5 text-[15px] font-light text-mocha">{current.body}</p>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

// ── The daily ritual ──────────────────────────────────────────────────────────
// Solid dark ground (the same `ink` tone as Glance / Proven / Reserve) rather
// than a photo behind the text, so the copy always reads clearly.
function Ritual() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const [i, pick] = useScrollCycle(RITUAL.states.length, sectionRef);
  const s = RITUAL.states[i];

  return (
    <section ref={sectionRef} id="ritual" className="bg-ink text-ink-on-dark" aria-labelledby="ritualHeading">
      {/* min-h + justify-between is what spread the three blocks apart on a phone: the
          forced 82vh had to go somewhere, so BOTH gaps the owner marked (2026-09-03)
          were the spare height, not the mt-* values. Releasing the min-height at the
          base breakpoint lets the blocks sit on their own margins; sm+ keeps the
          full-height composition exactly as signed off. */}
      <div className={`${container} flex min-h-0 flex-col justify-between py-[clamp(64px,10vw,120px)] sm:min-h-[82vh]`}>
        <div>
          <Eyebrow dark>{RITUAL.eyebrow}</Eyebrow>
          <h2 id="ritualHeading" className={`mt-4 max-w-[16ch] ${h2Size} font-light text-ink-on-dark`}>{RITUAL.heading}</h2>
        </div>
        {/* One paragraph, not two (owner, 2026-09-03 — "too much text"). Keeps the
            quote's words at the body size the second line used to have; the times
            it used to spell out are already in the tabs below. `key` restarts the
            fade on every change, whether scrolled or tapped. */}
        <div key={i} className="state-fade mt-6 max-w-[640px] sm:mt-10">
          <p className="max-w-[52ch] text-[19px] font-light text-ink-on-dark/80">{s.quote}</p>
        </div>
        <div className="mt-8 sm:mt-14">
          {/* Three across at EVERY width (owner, 2026-09-02 — they used to stack on
              mobile). At 390px the row is 350px wide, so with an 8px gap each tile
              gets 111.3px and the mobile type is stepped down to fit it on one line:
              "07:00 – 09:00" is the widest line and measures 104px at 13.65px Libre
              Baskerville, "FIRST LIGHT" 70px at 11px/0.08em, "Dawn stillness" 82px
              at 11px. The time is FLUID rather than a flat 14px because a flat 14px
              measures 107px and so wraps on a 375px iPhone (tile 106.3px) — the
              3.5vw track keeps ~6% clearance from 320px up. min-w-0 lets a tile
              shrink instead of pushing the row wider than the page, so any wider
              fallback face wraps inside its own column rather than causing a
              horizontal scroll. Everything reverts at sm+, so the desktop row is
              unchanged: gap-0, shared top rule, clamp(18px,2vw,24px) times. */}
          <div className="flex gap-2 border-white/15 sm:gap-0 sm:border-t" role="tablist" aria-label="Time of day">
            {RITUAL.states.map((st, idx) => (
              <button key={st.tab} onClick={() => pick(idx)} role="tab" aria-selected={idx === i}
                className={`min-w-0 flex-1 border-t pt-3.5 text-left transition-colors sm:-mt-px sm:px-4 sm:pt-5 ${idx === i ? "border-tussock text-ink-on-dark" : "border-white/15 text-ink-on-dark/55 hover:text-ink-on-dark"}`}>
                <span className="mb-2 block text-[11px] uppercase tracking-[0.08em] sm:tracking-[0.16em]">{st.tab}</span>
                <span style={{ fontFamily: "var(--font-display)" }} className="block text-[clamp(11px,3.5vw,14px)] font-light sm:text-[clamp(18px,2vw,24px)]">{st.time}</span>
                <span className="mt-1 block text-[11px] opacity-70 sm:text-[12px]">{st.note}</span>
              </button>
            ))}
          </div>
          {/* Replaces the sticky bottom bar (owner, 2026-09-03). Same lg: boundary
              the bar used, so phones and tablets keep a mid-page Reserve action and
              the desktop composition is untouched. */}
          <a
            href="#reserve"
            className="ui-label mt-8 flex w-full items-center justify-center rounded-pill bg-tussock px-8 py-4 text-ink transition-colors hover:bg-cream-deep lg:hidden"
          >
            Reserve your place
          </a>
        </div>
      </div>
    </section>
  );
}

// ── Contrast therapy ──────────────────────────────────────────────────────────
const NODE_POS = [
  { top: "0%", left: "50%" },
  { top: "50%", left: "100%" },
  { top: "100%", left: "50%" },
  { top: "50%", left: "0%" },
];
function Contrast() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const [i, setI] = useScrollCycle(CONTRAST.phases.length, sectionRef);
  const p = CONTRAST.phases[i];
  return (
    <section ref={sectionRef} className="bg-cream" aria-labelledby="contrastHeading">
      <Reveal className={`${container} ${sectionPad}`}>
        <div className="mb-14 max-w-[640px]">
          <Eyebrow>{CONTRAST.eyebrow}</Eyebrow>
          <h2 id="contrastHeading" className={`mt-4 mb-4 ${h2Size} font-light`}>{CONTRAST.heading}</h2>
          <p className="text-[19px] font-light text-mocha">{CONTRAST.intro}</p>
        </div>
        {/* items-start (not center) so the circular widget stays anchored to the top
            of the row and doesn't shift up/down as the right-hand copy changes
            height between phases. Widget is capped smaller on mobile so it (and its
            edge pins/labels) fit within the viewport. */}
        <div className="grid items-start gap-16 lg:grid-cols-[1.1fr_.9fr]">
          <div className="relative mx-auto aspect-square w-full max-w-[248px] sm:max-w-[420px]">
            <div className="absolute inset-0 rounded-full border border-dashed border-ink/25" />
            <div className="absolute inset-[18%] flex items-center justify-center rounded-full bg-cream-warm p-5 text-center shadow-[0_12px_40px_rgba(57,51,49,.1)]">
              <div>
                <b style={{ fontFamily: "var(--font-display)" }} className="mb-1.5 block text-[clamp(20px,2.4vw,28px)] font-light">{p.label}</b>
                <span className="text-[11px] uppercase tracking-[0.1em] text-mocha">Tap a phase</span>
              </div>
            </div>
            {CONTRAST.phases.map((ph, idx) => (
              <button key={ph.label} onClick={() => setI(idx)} aria-pressed={idx === i}
                className="absolute flex w-[80px] -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-2 sm:w-[104px]"
                style={{ top: NODE_POS[idx].top, left: NODE_POS[idx].left }}>
                <span className={`flex h-12 w-12 items-center justify-center rounded-full border text-[10px] uppercase tracking-[0.06em] transition-all duration-300 sm:h-16 sm:w-16 sm:text-[11px] ${idx === i ? "scale-105 border-ink bg-ink text-ink-on-dark" : "border-ink/25 bg-cream text-ink"}`}>{ph.node}</span>
                <span className="text-[10px] uppercase tracking-[0.08em] text-mocha sm:text-[11px]">{ph.label}</span>
              </button>
            ))}
          </div>
          <div className="min-h-[280px] lg:min-h-[360px]" aria-live="polite">
            {/* CONTRAST.note (the "general information only, not medical advice"
                line) was removed here on the owner's instruction, 2026-09-03. The
                copy it sat under still describes physiological effects, and clause
                10 of the Membership Terms has the buyer confirm they are physically
                capable and have sought medical advice — the string is left in
                content.ts so it can be put back in one line. */}
            <div className="mb-3.5"><Eyebrow>{p.eyebrow}</Eyebrow></div>
            <h3 style={{ fontFamily: "var(--font-display)" }} className="mb-4 text-[clamp(28px,4vw,48px)] font-light">{p.title}</h3>
            <p className="text-[19px] font-light text-mocha">{p.body}</p>
            <div className="mt-6 flex flex-wrap gap-7">
              {p.stats.map((st) => (
                <div key={st.label}>
                  <b style={{ fontFamily: "var(--font-display)" }} className="block text-[20px] font-light">{st.value}</b>
                  <small className="text-[11px] uppercase tracking-[0.08em] text-mocha">{st.label}</small>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

// ── Proven in Queenstown ──────────────────────────────────────────────────────
// Eyebrow / heading / intro + the stats bar only. The Google-reviews quote grid
// was removed in review (no real Bathe quotes to publish yet).
function Proven() {
  return (
    <section id="proven" className="bg-ink text-ink-on-dark" aria-labelledby="provenHeading">
      <Reveal className={`${container} ${sectionPad}`}>
        {/* Single column below lg, so gap-14 sat between the heading and the intro and
            mb-16 before the stats bar — both marked as too airy on a phone (owner,
            2026-09-03). Tightened at the base breakpoint; the lg two-column layout,
            where gap-14 is the horizontal gutter, is unchanged. */}
        <div className="mb-9 grid items-end gap-5 lg:mb-16 lg:grid-cols-[1.1fr_.9fr] lg:gap-14">
          <div>
            <Eyebrow dark>{PROVEN.eyebrow}</Eyebrow>
            <h2 id="provenHeading" className={`mt-4 ${h2Size} font-light text-ink-on-dark`}>{PROVEN.heading}</h2>
          </div>
          <p className="text-[19px] font-light text-ink-on-dark/78">{PROVEN.intro}</p>
        </div>
        <div className="grid border-y border-[color:var(--rule-on-dark)] md:grid-cols-3">
          {PROVEN.stats.map((st, idx) => {
            // Value / label / note block, reused whether or not the tile has a logo.
            const text = (
              <div>
                <b style={{ fontFamily: "var(--font-display)" }} className="block text-[clamp(30px,4vw,52px)] font-light">{st.value}</b>
                <span className="text-[11px] uppercase tracking-[0.08em] text-ink-on-dark/60">{st.label}</span>
                {st.note && <div className="mt-2.5 text-[13px] text-ink-on-dark/60">{st.note}</div>}
              </div>
            );
            return (
              <div key={st.label} className={`px-[clamp(20px,4vw,48px)] py-8 ${idx < 2 ? "border-b md:border-b-0 md:border-r" : ""} border-[color:var(--rule-on-dark)]`}>
                {st.image ? (
                  // Logo sits left of the text so the award tile stays the same
                  // height as the plain stat tiles beside it.
                  <div className="flex items-center gap-5">
                    <img src={st.image} alt="Winner of the NZIA Southern Architecture Award 2026" className="h-[92px] w-auto shrink-0 md:h-[112px]" />
                    {text}
                  </div>
                ) : text}
              </div>
            );
          })}
        </div>
      </Reveal>
    </section>
  );
}

// ── The film ──────────────────────────────────────────────────────────────────
// Replaced the "On the house" inclusions grid (owner, 2026-09-03) — see the note
// on FILM in content.ts for the entitlement copy that left the page with it.
//
// SELF-HOSTED. This was a YouTube embed through release 261 and its chrome could
// not be suppressed: controls=0 hides the control bar, but the player redraws its
// title / "Watch on YouTube" overlay on interaction and modestbranding has been
// deprecated. A plain <video> with no `controls` attribute has no chrome to hide
// in the first place, needs no third-party script on a page carrying live ad
// spend, and — unlike the embed, which YouTube bot-gates from our build host —
// can actually be verified playing.
//
// muted + playsInline remain mandatory: browsers refuse audible autoplay, and
// without playsInline iOS Safari takes the video fullscreen mid-scroll.
function Film() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const sectionRef = useRef<HTMLElement | null>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    const video = videoRef.current;
    const node = sectionRef.current;
    if (!video || !node) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    // The file ships as preload="none". It is 7.1 MiB, and preload="auto" had it
    // competing with the app bundle and the Maps stack for a narrow mobile pipe
    // from the first byte — measured at ~1.1s added to the checkout's own script
    // on a 1.6 Mbps connection. Warm it only once the page has finished loading
    // and the main thread is idle, so it never contends with first paint.
    const warm = () => {
      if (video.preload === "auto") return;
      video.preload = "auto";
      video.load();
    };
    const idle = () => {
      const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number }).requestIdleCallback;
      // Safari only gained requestIdleCallback recently — fall back to a timer.
      if (ric) ric(warm, { timeout: 3000 }); else setTimeout(warm, 1200);
    };
    if (document.readyState === "complete") idle();
    else window.addEventListener("load", idle, { once: true });

    // play() rejects if the browser declines (or if a pause interrupts it). That
    // is not an error worth surfacing — the poster simply stays up.
    const play = () => { warm(); void video.play().catch(() => {}); };

    const io = new IntersectionObserver(
      ([e]) => {
        // Any sliver on screen counts as visible: the film is only 219px tall on a
        // phone, so a ratio threshold would stop it on every entry and exit.
        if (e.intersectionRatio > 0) {
          // Rewind on the FIRST view only, so the opening frames are what people
          // see. Coming back to it later resumes rather than restarting.
          if (!startedRef.current) { startedRef.current = true; video.currentTime = 0; }
          play();
        } else if (startedRef.current) {
          video.pause();
        }
      },
      { threshold: [0, 0.01] },
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  return (
    // Full bleed, no eyebrow or heading (owner, 2026-09-03) — the film IS the
    // section, edge to edge. 16/9 matches the source, so it fills without bars.
    // The box is reserved before the video paints, so nothing below it shifts.
    <section ref={sectionRef} aria-label={FILM.title}>
      <div className="relative w-full overflow-hidden bg-ink" style={{ aspectRatio: "16 / 9" }}>
        {/* No `controls` attribute — that is the whole point of self-hosting. */}
        <video
          ref={videoRef}
          className="absolute inset-0 h-full w-full object-cover"
          src={FILM.src}
          poster={FILM.poster}
          muted
          loop
          playsInline
          preload="auto"
          disablePictureInPicture
          aria-label={FILM.title}
        />
      </div>
    </section>
  );
}

// ── Map ───────────────────────────────────────────────────────────────────────
function MapSection() {
  return (
    <section className="bg-ink text-ink-on-dark" aria-labelledby="mapHeading">
      <Reveal className={`${container} ${sectionPad}`}>
        <div className="mb-8">
          <Eyebrow dark>{MAP.eyebrow}</Eyebrow>
          <h2 id="mapHeading" className={`mt-4 ${h2Size} font-light text-ink-on-dark`}>{MAP.heading}</h2>
        </div>
        <MembershipMap />
      </Reveal>
    </section>
  );
}

// ── Opening countdown ─────────────────────────────────────────────────────────
// Counts down to the server-driven opening date (iso). Reserve renders this whenever an opening
// date exists; with no date at all it shows nothing (graceful fallback).
function Countdown({ iso }: { iso: string }) {
  // Placeholder shown for the single frame before the first tick (and if the date
  // is unreadable). A middot rather than the em dash it used to be — the page no
  // longer sets an em dash anywhere a visitor can see one.
  const [parts, setParts] = useState<{ d: string; h: string; m: string }>({ d: "·", h: "·", m: "·" });
  useEffect(() => {
    const target = new Date(iso).getTime();
    const tick = () => {
      const diff = target - Date.now();
      if (diff <= 0) { setParts({ d: "0", h: "0", m: "0" }); return; }
      setParts({
        d: String(Math.floor(diff / 86400000)),
        h: String(Math.floor((diff % 86400000) / 3600000)),
        m: String(Math.floor((diff % 3600000) / 60000)),
      });
    };
    tick();
    const t = setInterval(tick, 60000);
    return () => clearInterval(t);
  }, [iso]);
  return (
    <div className="mt-2 flex gap-6">
      {[{ v: parts.d, l: "Days" }, { v: parts.h, l: "Hours" }, { v: parts.m, l: "Mins" }].map((c) => (
        <div key={c.l} className="text-center">
          <b style={{ fontFamily: "var(--font-display)" }} className="block text-[28px] font-light">{c.v}</b>
          <small className="text-[10px] uppercase tracking-[0.1em] text-ink-on-dark/55">{c.l}</small>
        </div>
      ))}
    </div>
  );
}

// ── Reserve (copy + countdown + real checkout) ────────────────────────────────
function Reserve({ remaining, opening }: { remaining: number | null; opening: OpeningState }) {
  return (
    <section id="reserve" className="bg-ink text-ink-on-dark" aria-labelledby="reserveHeading">
      <Reveal className={`${container} ${sectionPad}`}>
        <div className="grid gap-16 lg:grid-cols-2">
          <div>
            <Eyebrow dark>{RESERVE.eyebrow}</Eyebrow>
            <h2 id="reserveHeading" className={`mt-4 mb-5 ${h2Size} font-light text-ink-on-dark`}>{RESERVE.heading}</h2>
            <p className="mb-7 text-[19px] font-light text-ink-on-dark/78">{RESERVE.intro}</p>
            <div className="mb-8 flex flex-col border-t border-[color:var(--rule-on-dark)]">
              {RESERVE.steps.map((st) => (
                <div key={st.n} className="flex gap-[18px] border-b border-[color:var(--rule-on-dark)] py-[18px]">
                  <span className="shrink-0 pt-0.5 text-[12px] text-tussock">{st.n}</span>
                  <div>
                    <b className="mb-0.5 block text-[15px] font-medium">{st.title}</b>
                    <span className="text-[13px] text-ink-on-dark/60">{st.body}</span>
                  </div>
                </div>
              ))}
            </div>
            {opening.iso && (
              <>
                <Eyebrow dark>Opening countdown</Eyebrow>
                <Countdown iso={opening.iso} />
              </>
            )}
            <p className="mt-6 text-[13px] font-light text-ink-on-dark/70">
              Prefer to talk first? <a href={`mailto:${CONFIG.contactEmail}`} className="text-tussock underline">{CONFIG.contactEmail}</a>
            </p>
            <p className="mt-2 text-[13px] font-light text-ink-on-dark/70">
              Before you reserve, read the <a href={CONFIG.termsUrl} target="_blank" rel="noopener noreferrer" className="text-tussock underline">Thermae100 Membership Terms</a>.
            </p>
          </div>
          <MembershipCheckout remaining={remaining} />
        </div>
      </Reveal>
    </section>
  );
}

// ── FAQ ───────────────────────────────────────────────────────────────────────
function Faq() {
  return (
    <section id="faq" className="bg-cream" aria-labelledby="faqHeading">
      <Reveal className={`${container} ${sectionPad}`}>
        <div className="mb-12 max-w-[640px]">
          <Eyebrow>Questions</Eyebrow>
          <h2 id="faqHeading" className={`mt-4 ${h2Size} font-light`}>Before you reserve.</h2>
        </div>
        <MembershipFaqs items={FAQS} />
      </Reveal>
    </section>
  );
}

// The sticky bottom CTA bar was removed on 2026-09-03 (owner: "hide the bottom nav
// popup on mobile"). Its job — a reachable Reserve action partway down a long page
// — moved into the daily ritual section as an inline button at the same
// breakpoint, so small screens still get a mid-page CTA without a bar covering the
// content. `scarcityLabel` in content.ts lost its only caller with it.

// ── Page body ─────────────────────────────────────────────────────────────────
export function MembershipLanding() {
  const [remaining, setRemaining] = useState<number | null>(null);
  // `total` was only ever read by the sticky bar's scarcity line; with that gone
  // nothing on the page renders it, so it is no longer tracked. The API still
  // returns it — add the state back if a "N of 100" treatment returns.
  // Opening date, server-driven (venues.opens_on via /api/membership/opening). No hard-coded
  // fallback: the countdown simply waits for the fetch and stays hidden if it fails.
  const [opening, setOpening] = useState<OpeningState>({ iso: null });

  const load = useCallback(() => {
    fetch("/api/membership/availability")
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!j) return;
        if (typeof j.remaining === "number") setRemaining(j.remaining);
      })
      .catch(() => { /* stays "—" — faithful to the mockup's default */ });

    fetch("/api/membership/opening")
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!j) return; // 404 → no countdown
        setOpening({ iso: typeof j.iso === "string" && j.iso ? j.iso : null });
      })
      .catch(() => { /* no countdown */ });
  }, []);
  useEffect(() => { load(); }, [load]);

  // Funnel: view_item once per /100 view (GTM → GA4 view_item + Meta ViewContent), and
  // mint/refresh the purchase_event_id up-front so it persists per-tab through the Verifone
  // round-trip and the /100/confirmed purchase fire can dedup against it.
  useEffect(() => {
    captureAttribution();
    trackClientEvent("view_item", {
      ecommerce: {
        currency: "NZD",
        value: CONFIG.priceMembership,
        items: [{ item_id: "founder_100", item_name: "100 by Aluume — Founder Membership", item_category: "membership" }],
      },
    });
  }, []);

  return (
    <main id="main">
      {/* Scoped keyframes — hotspot pulse + the slow hero pan (Ken Burns). No
          global stylesheet change; both honour prefers-reduced-motion. */}
      <style>{"@keyframes hs-pulse{0%{transform:scale(.7);opacity:.9}100%{transform:scale(1.9);opacity:0}}@keyframes hero-pan{0%{transform:scale(1.06) translate3d(0,0,0)}100%{transform:scale(1.14) translate3d(-2.2%,-1.6%,0)}}.hero-pan{transform:scale(1.06);transform-origin:60% 40%;will-change:transform;animation:hero-pan 42s ease-in-out infinite alternate}@media (prefers-reduced-motion:reduce){.hero-pan{animation:none}}@keyframes state-fade{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}.state-fade{animation:state-fade 380ms ease-out}@media (prefers-reduced-motion:reduce){.state-fade{animation:none}}"}</style>
      <Hero />
      <Glance />
      <Showcase />
      <Ritual />
      <Contrast />
      <Proven />
      <Film />
      <Reserve remaining={remaining} opening={opening} />
      <Faq />
      <MapSection />
    </main>
  );
}
